import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { AsOf, FxStamp } from "../components/AsOf";
import { DataStamp } from "../components/DataLabel";

const usd = (n) =>
  n >= 1e9 ? `$${(n / 1e9).toFixed(2)}bn` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}m` : `$${Math.round(n).toLocaleString()}`;
const ksh = (n) =>
  n >= 1e9 ? `KSh ${(n / 1e9).toFixed(1)}bn` : n >= 1e6 ? `KSh ${(n / 1e6).toFixed(0)}m` : `KSh ${Math.round(n).toLocaleString()}`;

export default function FootballEconomy() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Kenyan football as an economy — what the built game gives back",
    description:
      "If the eighteen counties are built, what Kenyan football contributes to GDP every year: grassroots participation, facilities and matchday, player trading, the national game and football tourism, line by line with the jobs and the identity case.",
    image: cardUrl("economy"),
  });

  useEffect(() => {
    api.get("/football-economy")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the football economy model. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="economy-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="economy-loading" />;

  const t = d.totals;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="football-economy-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Football financials</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-7 max-w-3xl">{d.standfirst}</p>
          <FxStamp fx={d.fx} testId="economy-fx-stamp" />
        </Reveal>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="economy-headlines">
          {[
            ["Every year, at maturity", usd(t.annual_usd), `${ksh(t.annual_ksh)} · ${t.pct_of_gdp}% of GDP`],
            ["Cash through the game", usd(t.cash_usd), `revenue, trading and tourism · ${usd(t.social_usd)} social value besides`],
            ["Modelled cash surplus against the build", `${t.cash_return_pct}%`, `a year on ${usd(t.capital_usd)} built · surplus matches the build cost in ${t.payback_years} years`],
            ["Jobs in the built game", t.jobs_total.toLocaleString(), `plus ${d.jobs.coaching_places.toLocaleString()} coaching places`],
          ].map(([label, value, note], i) => (
            <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`economy-headline-${i}`}>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
              <p className="mt-4 font-display text-3xl font-bold leading-none text-white md:text-4xl">{value}</p>
              <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
            </div>
          ))}
        </div>

        <section className="mt-12" data-testid="economy-blocks">
          <h2 className="display-3 border-b border-white/15 pb-4">Five Blocks, Five Sources</h2>
          <div className="mt-6 border border-zinc-800">
            {d.blocks.map((b, i) => (
              <div
                key={b.key}
                className="border-b border-zinc-900 bg-[#0b0b0b] p-6 md:p-7"
                data-testid={`economy-block-${b.key}`}
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-baseline md:justify-between md:gap-10">
                  <div className="min-w-0">
                    <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-zinc-600">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <p className="mt-3 font-display text-xl font-bold uppercase leading-tight text-white md:text-2xl">
                      {b.label}
                    </p>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">{b.basis}</p>
                    <p className="mt-3 max-w-2xl text-xs leading-relaxed text-zinc-500">{b.note}</p>
                    <AsOf kind={b.kind} publisher={b.publisher} url={b.url} testId={`economy-block-source-${b.key}`} />
                    <DataStamp source={b} testId={`economy-block-stamp-${b.key}`} />
                  </div>
                  <div className="shrink-0 md:text-right">
                    <p className="font-display text-2xl font-bold leading-none text-ochre md:text-3xl">
                      {usd(b.annual_usd)}
                    </p>
                    <p className="mono mt-2 text-[0.56rem] uppercase tracking-[0.14em] text-zinc-500">
                      {ksh(b.annual_ksh)} · {b.share_pct}% of the total
                    </p>
                  </div>
                </div>
                <div className="mt-5 h-1 w-full bg-zinc-900">
                  <div className="h-1 bg-maasai" style={{ width: `${b.share_pct}%` }} />
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-baseline justify-between gap-4 bg-[#141414] p-6" data-testid="economy-total-row">
              <p className="font-display text-lg font-bold uppercase text-white md:text-xl">
                Every year, once it is built
              </p>
              <p className="font-display text-2xl font-bold text-white md:text-3xl">
                {usd(t.annual_usd)} <span className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
                  {ksh(t.annual_ksh)}
                </span>
              </p>
            </div>
          </div>
        </section>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_1fr] [&>*]:min-w-0">
          <section data-testid="economy-ramp">
            <h2 className="display-3 border-b border-white/15 pb-4">It Does Not Land On Day One</h2>
            <div className="mt-6 overflow-x-auto border border-zinc-800">
              <table className="w-full min-w-[420px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                    {["Year", "Built", "Contribution", "Of GDP"].map((h) => (
                      <th key={h} className="px-4 py-3 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-zinc-300">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.ramp.map((r) => (
                    <tr key={r.year} className="border-b border-zinc-900" data-testid={`economy-ramp-${r.year}`}>
                      <td className="mono px-4 py-2.5 text-sm text-white">{r.year}</td>
                      <td className="mono px-4 py-2.5 text-xs text-zinc-400">{r.share_pct}%</td>
                      <td className="mono px-4 py-2.5 text-xs text-ochre">{usd(r.annual_usd)}</td>
                      <td className="mono px-4 py-2.5 text-xs text-zinc-400">{r.pct_of_gdp}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section data-testid="economy-jobs">
            <h2 className="display-3 border-b border-white/15 pb-4">What It Employs And Organises</h2>
            <div className="mt-6 grid gap-px bg-zinc-800 sm:grid-cols-2 [&>*]:min-w-0">
              {[
                ["Facility jobs", d.jobs.facility_jobs],
                ["Professional club jobs", d.jobs.professional_jobs],
                ["Coaching places", d.jobs.coaching_places],
                ["Referee places", d.jobs.referee_places],
                ["Teams organised", d.jobs.teams],
                ["Matches a year", d.jobs.matches_per_year],
              ].map(([label, value], i) => (
                <div key={label} className="bg-[#0b0b0b] p-5" data-testid={`economy-job-${i}`}>
                  <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
                  <p className="mt-3 font-display text-2xl font-bold leading-none text-white">
                    {value.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs leading-relaxed text-zinc-500" data-testid="economy-jobs-note">
              Wages are reported here but never added to the total above: they already sit inside the operating
              revenue, and counting them twice would flatter the case.
            </p>
          </section>
        </div>

        <section className="mt-14" data-testid="economy-identity">
          <p className="eyebrow">The part you cannot add up</p>
          <h2 className="display-2 mt-4 max-w-3xl">What It Would Mean To Be Kenyan</h2>
          <p className="body-copy mt-5 max-w-3xl">{d.identity.standfirst}</p>
          <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-2">
            {d.identity.points.map((p, i) => (
              <div key={p.heading} className="bg-[#0b0b0b] p-6 md:p-8" data-testid={`economy-identity-${i}`}>
                <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-zinc-600">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-4 font-display text-xl font-bold uppercase leading-tight text-white md:text-2xl">
                  {p.heading}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-px grid gap-px bg-zinc-800 sm:grid-cols-3">
            {[
              ["Diaspora remittances a year", usd(d.identity.remittances_usd)],
              ["One per cent of them", usd(d.identity.one_percent_usd)],
              ["Kenya's median age", d.identity.median_age],
            ].map(([label, value], i) => (
              <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`economy-identity-stat-${i}`}>
                <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
                <p className="mt-3 font-display text-3xl font-bold leading-none text-ochre">{value}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.1fr_1fr]">
          <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="economy-method">
            <p className="overline text-ochre">Method</p>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.method}</p>
          </div>
          <div>
            <div className="border border-zinc-800 bg-[#0b0b0b] p-6" data-testid="economy-assumptions">
              <p className="overline text-ochre">Assumptions</p>
              <ul className="mt-4 space-y-3">
                {d.assumptions.map((a, i) => (
                  <li key={i} className="flex gap-3 text-xs leading-relaxed text-zinc-400">
                    <span className="mono shrink-0 text-ochre">{String(i + 1).padStart(2, "0")}</span>
                    {a}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 border border-zinc-800" data-testid="economy-sources">
              {d.sources.map((s, i) => (
                <a
                  key={s.url + i}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-4 border-b border-zinc-900 bg-[#0b0b0b] p-4 transition-colors hover:bg-[#141414]"
                  data-testid={`economy-source-${i}`}
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-white">{s.label}</span>
                    <span className="mono mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                      {s.publisher} · {s.kind}
                    </span>
                  </span>
                  <ExternalLink size={12} className="shrink-0 text-ochre" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/value-case" className="btn-primary" data-testid="economy-value-link">
            The value case behind it <ArrowRight size={14} />
          </Link>
          <Link to="/money" className="btn-ghost" data-testid="economy-money-link">
            What already moves through the game
          </Link>
          <Link to="/sources" className="btn-ghost" data-testid="economy-sources-link">
            Sources and Method
          </Link>
        </div>
      </div>
    </div>
  );
}
